import React, { useMemo, useState, useEffect, useRef } from 'react';
import './PostPreviewPanel.css';

const detectNet = (platformName) => {
  const t = (platformName || '').toLowerCase();
  if (t.includes('tiktok')) return 'tiktok';
  if (t.includes('facebook')) return 'facebook';
  return 'instagram'; // default razonable (incluye Instagram y cualquier otra)
};

const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const initials = (name) => (name || 'G').trim().charAt(0).toUpperCase();

// Medidas oficiales 2026 por red social y tipo de contenido.
// Instagram: feed (imagen/video/carrusel) → 4:5 (1080x1350, el formato
//   recomendado desde 2026 porque ocupa más pantalla que el 1:1 clásico).
//   Reel/Historia → 9:16 (1080x1920), pantalla completa vertical.
// TikTok: siempre 9:16 (1080x1920) — es nativamente vertical.
// Facebook: feed → 4:5 (1080x1350). Reel/Historia → 9:16 (1080x1920)
//   (desde marzo 2026 Meta unificó Reels e Historias en un solo
//   "safe zone" 9:16 compartido entre Facebook e Instagram).
const ASPECT_SPECS = {
  instagram: {
    story: { ratio: '9 / 16', label: '1080×1920 · 9:16' },
    reel: { ratio: '9 / 16', label: '1080×1920 · 9:16' },
    default: { ratio: '4 / 5', label: '1080×1350 · 4:5' },
  },
  facebook: {
    story: { ratio: '9 / 16', label: '1080×1920 · 9:16' },
    reel: { ratio: '9 / 16', label: '1080×1920 · 9:16' },
    default: { ratio: '4 / 5', label: '1080×1350 · 4:5' },
  },
  tiktok: {
    default: { ratio: '9 / 16', label: '1080×1920 · 9:16' },
  },
};

const getAspectSpec = (net, materialType) => {
  const specsForNet = ASPECT_SPECS[net] || ASPECT_SPECS.instagram;
  const key = (materialType || '').toLowerCase();
  return specsForNet[key] || specsForNet.default;
};

/**
 * Panel de preview en vivo del post, imitando la interfaz real de
 * Instagram / TikTok / Facebook. Se actualiza a medida que se
 * escribe el copy o se suben archivos — sin esperar aprobación.
 * Respeta las medidas/aspect ratio oficiales de cada red y tipo
 * de contenido (feed, reel, historia).
 */
const PostPreviewPanel = ({ platformName, accountName, copyHtml, hashtags, mentions, mediaFiles, materialType }) => {
  const detected = detectNet(platformName);
  const [netOverride, setNetOverride] = useState(null);
  const net = netOverride || detected;
  const aspectSpec = getAspectSpec(net, materialType);

  const caption = useMemo(() => {
    const base = stripHtml(copyHtml).trim();
    const extra = [mentions, hashtags].filter(Boolean).join(' ');
    return [base, extra].filter(Boolean).join('\n\n');
  }, [copyHtml, hashtags, mentions]);

  // Carrusel: cuando el tipo de material es "carousel", se muestran TODOS
  // los contenidos aprobados (no solo el primero) y se puede deslizar
  // entre ellos, igual que en la red social.
  const isCarouselType = (materialType || '').toLowerCase() === 'carousel';
  const isStorySlides = (materialType || '').toLowerCase() === 'story';

  const approvedMedia = useMemo(() => {
    if (!mediaFiles || mediaFiles.length === 0) return [];
    const approved = mediaFiles.filter((m) => m.approved);
    return approved.length > 0 ? approved : mediaFiles;
  }, [mediaFiles]);

  // Carrusel también aplica a Historia cuando hay más de un contenido
  // aprobado: se muestran todos con slide, igual que en la app real.
  const isCarousel = (isCarouselType || isStorySlides) && approvedMedia.length > 1;

  const media = useMemo(() => {
    if (approvedMedia.length === 0) return null;
    return approvedMedia[0];
  }, [approvedMedia]);

  const [slideIndex, setSlideIndex] = useState(0);
  useEffect(() => {
    setSlideIndex(0);
  }, [mediaFiles, materialType]);

  const currentSlide = isCarousel
    ? approvedMedia[Math.min(slideIndex, approvedMedia.length - 1)]
    : media;

  const goToSlide = (i) => {
    if (!isCarousel) return;
    const total = approvedMedia.length;
    setSlideIndex(((i % total) + total) % total);
  };
  const goNext = () => goToSlide(slideIndex + 1);
  const goPrev = () => goToSlide(slideIndex - 1);

  // Swipe táctil, como en el feed real.
  const touchStartX = useRef(null);
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(deltaX) < 40) return;
    if (deltaX < 0) goNext(); else goPrev();
  };

  const username = (accountName || 'gevescl').toLowerCase().replace(/\s+/g, '');

  // Historia (Instagram/Facebook): formato pantalla completa 9:16, distinto
  // al feed clásico — barra de progreso arriba, header sobre la imagen,
  // caja de "Enviar mensaje" abajo, como en la app real.
  const isStoryType = (materialType || '').toLowerCase() === 'story';

  const renderMediaItem = (item) => {
    if (!item) return <span>Sin contenido adjunto</span>;
    if (item.file_type === 'video') {
      return <video src={item.file_url} muted loop autoPlay playsInline />;
    }
    return <img src={item.file_url} alt="preview" />;
  };

  const mediaContent = () => {
    if (!isCarousel) return renderMediaItem(currentSlide);
    return (
      <>
        {renderMediaItem(currentSlide)}
        <div className="pp-carousel-count">{slideIndex + 1}/{approvedMedia.length}</div>
        <button type="button" className="pp-carousel-arrow pp-carousel-arrow--prev" onClick={(e) => { e.stopPropagation(); goPrev(); }} aria-label="Anterior">
          <i className="bi bi-chevron-left" />
        </button>
        <button type="button" className="pp-carousel-arrow pp-carousel-arrow--next" onClick={(e) => { e.stopPropagation(); goNext(); }} aria-label="Siguiente">
          <i className="bi bi-chevron-right" />
        </button>
        <div className="pp-carousel-dots">
          {approvedMedia.map((_, i) => (
            <span key={i} className={`pp-carousel-dot ${i === slideIndex ? 'active' : ''}`} />
          ))}
        </div>
      </>
    );
  };

  const captionContent = (withHandlePrefix) => {
    if (!caption) return <span className="pp-caption-empty">Sin copy todavía…</span>;
    return (
      <>
        {withHandlePrefix && <span className="pp-username">{username}</span>}
        {caption}
      </>
    );
  };

  return (
    <div className="post-preview-panel">
      <div className="pp-network-switch">
        {['instagram', 'tiktok', 'facebook'].map((n) => (
          <button
            key={n}
            type="button"
            className={`pp-net-btn ${net === n ? 'active' : ''}`}
            onClick={() => setNetOverride(n)}
            title={n === 'instagram' ? 'Instagram' : n === 'tiktok' ? 'TikTok' : 'Facebook'}
            aria-label={n === 'instagram' ? 'Instagram' : n === 'tiktok' ? 'TikTok' : 'Facebook'}
          >
            <i className={`bi bi-${n}`} />
          </button>
        ))}
      </div>

      {net === 'instagram' && isStoryType && (
        <div className="pp-card pp-net-instagram pp-story">
          <div
            className={`pp-media pp-story-media ${isCarousel ? 'pp-media--carousel' : ''}`}
            style={{ aspectRatio: aspectSpec.ratio }}
            onTouchStart={isCarousel ? onTouchStart : undefined}
            onTouchEnd={isCarousel ? onTouchEnd : undefined}
          >
            <div className="pp-story-progress">
              {(isCarousel ? approvedMedia : [currentSlide]).map((_, i) => (
                <span
                  key={i}
                  className={`pp-story-progress-bar ${(!isCarousel || i === slideIndex) ? 'pp-story-progress-bar--active' : ''}`}
                />
              ))}
            </div>
            <div className="pp-story-header">
              <div className="pp-avatar pp-story-avatar">{initials(username)}</div>
              <div className="pp-story-header-text">
                <span className="pp-username">{username}</span>
                <span className="pp-sub">21 min</span>
              </div>
              <i className="bi bi-three-dots" />
              <i className="bi bi-x-lg" />
            </div>

            {mediaContent()}

            {caption && (
              <div className="pp-story-caption-overlay">{captionContent(false)}</div>
            )}

            <div className="pp-story-footer">
              <div className="pp-story-input">Enviar mensaje...</div>
              <i className="bi bi-heart" />
              <i className="bi bi-chat" />
              <i className="bi bi-send" />
            </div>
          </div>
        </div>
      )}

      {net === 'instagram' && !isStoryType && (
        <div className="pp-card pp-net-instagram">
          <div className="pp-header">
            <div className="pp-avatar">{initials(username)}</div>
            <div className="pp-username">{username}</div>
          </div>
          <div
            className={`pp-media ${isCarousel ? 'pp-media--carousel' : ''}`}
            style={{ aspectRatio: aspectSpec.ratio }}
            onTouchStart={isCarousel ? onTouchStart : undefined}
            onTouchEnd={isCarousel ? onTouchEnd : undefined}
          >
            {mediaContent()}
          </div>
          <div className="pp-actions pp-actions-ig">
            <div className="pp-actions-left">
              <i className="bi bi-heart" />
              <i className="bi bi-chat" />
              <i className="bi bi-send" />
            </div>
            <i className="bi bi-bookmark" />
          </div>
          <div className="pp-caption">{captionContent(true)}</div>
        </div>
      )}

      {net === 'facebook' && (
        <div className="pp-card pp-net-facebook">
          <div className="pp-header">
            <div className="pp-avatar">{initials(username)}</div>
            <div>
              <div className="pp-username">{username}</div>
              <div className="pp-sub">Ahora</div>
            </div>
          </div>
          <div className="pp-caption">{captionContent(false)}</div>
          <div
            className={`pp-media ${isCarousel ? 'pp-media--carousel' : ''}`}
            style={{ aspectRatio: aspectSpec.ratio }}
            onTouchStart={isCarousel ? onTouchStart : undefined}
            onTouchEnd={isCarousel ? onTouchEnd : undefined}
          >
            {mediaContent()}
          </div>

          <div className="pp-fb-reactions">
            <span className="pp-fb-reaction-icons">
              <span className="pp-fb-reaction-icon pp-fb-reaction-like"><i className="bi bi-hand-thumbs-up-fill" /></span>
              <span className="pp-fb-reaction-icon pp-fb-reaction-love"><i className="bi bi-heart-fill" /></span>
            </span>
          </div>

          <div className="pp-actions pp-actions-fb">
            <span><i className="bi bi-hand-thumbs-up" /> Me gusta</span>
            <span><i className="bi bi-chat" /> Comentar</span>
            <span><i className="bi bi-share" /> Compartir</span>
          </div>
        </div>
      )}

      {net === 'tiktok' && (
        <div className="pp-card pp-net-tiktok">
          <div className="pp-tt-tabs">
            <span>Siguiendo</span>
            <span className="pp-tt-tab-active">Para ti</span>
          </div>

          <div className="pp-media" style={{ aspectRatio: aspectSpec.ratio }}>
            {mediaContent()}

            <div className="pp-tt-side-actions">
              <div className="pp-tt-avatar-wrap">
                <div className="pp-avatar pp-tt-avatar">{initials(username)}</div>
                <span className="pp-tt-follow">+</span>
              </div>
              <div className="pp-tt-action">
                <i className="bi bi-heart-fill" />
                <span>0</span>
              </div>
              <div className="pp-tt-action">
                <i className="bi bi-chat-fill" />
                <span>0</span>
              </div>
              <div className="pp-tt-action">
                <i className="bi bi-reply-fill" />
                <span>0</span>
              </div>
              <div className="pp-tt-disc">
                <i className="bi bi-music-note" />
              </div>
            </div>

            <div className="pp-tt-bottom-info">
              <div className="pp-username">@{username}</div>
              <div className="pp-caption">{captionContent(false)}</div>
              <div className="pp-tt-music">
                <i className="bi bi-music-note-beamed" /> sonido original
              </div>
            </div>
          </div>

          <div className="pp-tt-navbar">
            <i className="bi bi-house-fill" />
            <i className="bi bi-people-fill" />
            <span className="pp-tt-nav-plus"><i className="bi bi-plus-lg" /></span>
            <i className="bi bi-chat-square-text" />
            <i className="bi bi-person-fill" />
          </div>
        </div>
      )}

      <div className="pp-format-label">Formato: {aspectSpec.label}</div>
    </div>
  );
};

export default PostPreviewPanel;
